import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useCustomerInvoices } from "@/hooks/useCustomerPortal";
import { useCompanyProfile } from "@/hooks/useCompanyProfile";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState } from "react";
import { Search, Receipt, Printer } from "lucide-react";
import { ExportToolbar } from "@/components/shared/ExportToolbar";
import { exportPdf } from "@/lib/export";
import { InvoicePrintTemplate } from "@/components/shared/print-templates/InvoicePrintTemplate";

const fmt = (n: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

const statusColor = (s: string) => {
  switch (s) {
    case "paid": return "bg-green-500/10 text-green-700 border-green-200";
    case "sent": case "approved": return "bg-blue-500/10 text-blue-700 border-blue-200";
    case "overdue": return "bg-destructive/10 text-destructive border-destructive/20";
    case "partially_paid": return "bg-yellow-500/10 text-yellow-700 border-yellow-200";
    default: return "";
  }
};

const CustomerInvoices = () => {
  const { data: invoices, isLoading } = useCustomerInvoices();
  const { data: companyProfile } = useCompanyProfile();
  const [search, setSearch] = useState("");
  const isMobile = useIsMobile();

  const handlePrint = (inv: any) =>
    exportPdf(
      <InvoicePrintTemplate
        data={{
          invoice_number: inv.invoice_number,
          invoice_date: inv.invoice_date,
          due_date: inv.due_date,
          status: inv.status,
          total: inv.total,
          amount_paid: inv.amount_paid,
          company_name: companyProfile?.name,
          company_logo_url: companyProfile?.logo_url,
        }}
      />,
      `Invoice ${inv.invoice_number}`
    );

  const filtered = (invoices ?? []).filter((i) =>
    i.invoice_number.toLowerCase().includes(search.toLowerCase())
  );

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Invoices</h1>
        <p className="text-muted-foreground text-sm">{filtered.length} invoice{filtered.length !== 1 ? "s" : ""}</p>
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search by invoice #..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        {filtered.length > 0 && (
          <ExportToolbar entityType="invoices" data={filtered} isCustomerPortal />
        )}
      </div>

      {filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground"><Receipt className="mx-auto h-10 w-10 mb-2 opacity-40" /><p>No invoices available</p></CardContent></Card>
      ) : isMobile ? (
        <div className="space-y-3">
          {filtered.map((inv) => (
            <Card key={inv.id}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{inv.invoice_number}</span>
                  <Badge variant="outline" className={`capitalize text-xs ${statusColor(inv.status)}`}>{inv.status.replace("_", " ")}</Badge>
                </div>
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>{inv.invoice_date}</span>
                  <span>Due: {inv.due_date || "—"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-lg font-semibold">{fmt(inv.total)}</p>
                    <p className="text-xs text-muted-foreground">Paid: {fmt(inv.amount_paid)}</p>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8" title="Print Invoice" onClick={() => handlePrint(inv)}>
                    <Printer className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Paid</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                    <TableCell className="text-muted-foreground">{inv.invoice_date}</TableCell>
                    <TableCell className="text-muted-foreground">{inv.due_date || "—"}</TableCell>
                    <TableCell className="text-right font-medium">{fmt(inv.total)}</TableCell>
                    <TableCell className="text-right">{fmt(inv.amount_paid)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`capitalize text-xs ${statusColor(inv.status)}`}>{inv.status.replace("_", " ")}</Badge>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-7 w-7" title="Print Invoice" onClick={() => handlePrint(inv)}>
                        <Printer className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CustomerInvoices;
